import { useCallback } from "react";
import { useCachedQuery } from "./useCachedQuery";
import { fetchTenantModelUsage, type ModelUsage } from "./useAdminStatus";
import { instanceModelUsageResource } from "../lib/adminQueries";
import { rangeToWindowHours, type InstanceRange } from "../lib/instanceDetailRange";

export type ModelUsageResult = {
  status: "loading" | "ready" | "error";
  usage: ModelUsage | null;
  error: string | null;
  isFetching: boolean;
};

export const useModelUsage = (tenantId: string, range: InstanceRange): ModelUsageResult => {
  const hours = rangeToWindowHours(range);
  const fetcher = useCallback(
    (signal: AbortSignal): Promise<ModelUsage> => fetchTenantModelUsage(tenantId, hours, signal),
    [tenantId, hours],
  );
  const { status, data, error, isFetching } = useCachedQuery<ModelUsage>({
    // Same rule as useInstanceMetrics: cache only the default window so a
    // 30d total never paints as the 24h figure on a cold cache.
    cacheResource: range === "24h" ? instanceModelUsageResource(tenantId) : null,
    queryKey: `/api/admin/claws/${tenantId}/model-usage?hours=${hours}`,
    fetcher,
  });
  return { status, usage: data, error, isFetching };
};
